import classNames from 'classnames'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import React, { FC, useEffect, useMemo, useState } from 'react'

import ArrowBack from '../../../assets/icons/arrow.svg'
import Arrow from '../../../assets/icons/seo-tools/arrow.svg'
import routes from '../../../utils/routes'
import { windowOpen } from '../../../utils/stringUtils'
import { BlogPostConfig, GhostPost } from '../../../utils/types'
import blogPostStyles from '../../blog-post/BlogPostContainer/BlogPostContainer.module.scss'
import CommonFooter from '../../layout/CommonFooter/CommonFooter'
import CommonHeaderLight, { HEADER_MODES } from '../../layout/CommonHeader/CommonHeaderLight'
import GuideHeaderText from '../../widgets/GuideHeader/GuideHeaderText'
import IconsLoader from '../../widgets/IconsLoader/IconsLoader'
import { ISeoTool, seoTools, seoToolsGroups } from '../seo-tools'
import { getSeoToolUrl } from '../seo-tools-raw'
import styles from './SeoToolContainer.module.scss'

const ContentsTool = dynamic(() => import('../../widgets/ContentsTool/ContentsTool'))

type Props = {
  tool: string
  post: GhostPost
  postConfig: BlogPostConfig
}

const SeoToolContainer: FC<Props> = ({ tool, post, postConfig }) => {
  const [seoTool, setSeoTool] = useState<ISeoTool | null>(null)
  const [seoToolGroup, setSeoToolGroup] = useState<ISeoTool[] | null>(null)

  const ToolComponent = useMemo(() => {
    if (!seoTool) {
      return null
    }
    return dynamic(seoTool.tool)
  }, [seoTool])

  useEffect(() => {
    const newSeoTool = Object.values(seoTools).find((t) => t.link === getSeoToolUrl(tool)) || null

    if (!newSeoTool) return

    setSeoTool(newSeoTool)
    setSeoToolGroup(
      Object.values(seoToolsGroups).find((group) => group.some((t) => t.link === newSeoTool.link)) || null,
    )
  }, [])

  if (!seoTool || !seoToolGroup) return null

  const renderBackButton = () => {
    return (
      <div className={styles.backButton} onClick={() => windowOpen(routes.FREE_SEO_TOOLS, '_parent')}>
        <ArrowBack />
        <div className={styles.toolIcon}>
          <Image src={'/static/images/seo-tools/free-seo-tools.png'} alt={'icon'} layout='fill' />
        </div>
        Free Music Tools
      </div>
    )
  }

  const renderToolsListButton = () => {
    return (
      <div className={styles.toolsList}>
        {Object.entries(seoToolsGroups).map(([key, tools], index) => (
          <React.Fragment key={`group_${index}`}>
            <div className={styles.groupTitle}>{key}:</div>

            {tools.map((tool, i) => (
              <React.Fragment key={`tool_${index}_${i}`}>
                <div
                  className={classNames(styles.seoTool, { [styles.active]: tool.link === seoTool.link })}
                  onClick={() => {
                    if (tool.link !== seoTool.link) windowOpen(tool.link, '_parent')
                  }}
                >
                  <div className={styles.toolIcon}>
                    <Image src={tool.icon} alt={tool.title} layout='fill' />
                  </div>{' '}
                  <div className={styles.seoToolTitle}>{tool.title}</div>
                </div>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <div className={classNames(styles.container, blogPostStyles.blog)} id='seoToolContainer'>
      <CommonHeaderLight className={styles.header} mode={HEADER_MODES.GUIDE} color='#141618' scrollable />

      <div className={styles.content}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>{seoTool.title}</div>
          <div className={styles.subtitle}>{seoTool.desc}</div>
        </div>

        {renderBackButton()}

        <div className={styles.tools}>
          {seoToolGroup.map((st, index) => (
            <React.Fragment key={`st_${index}`}>
              <div
                className={classNames(styles.tool, { [styles.active]: st.link !== seoTool.link })}
                onClick={() => {
                  if (st.link !== seoTool.link) windowOpen(st.link, '_parent')
                }}
              >
                {st.title}
              </div>
              {index !== seoToolGroup.length - 1 ? <div className={styles.divider}>/</div> : null}
            </React.Fragment>
          ))}
        </div>

        <div className={classNames(styles.toolContainer, styles[tool])}>
          <Arrow className={styles.toolArrow} />

          <div className={styles.links}>
            {renderBackButton()}
            {renderToolsListButton()}
          </div>
          {/* @ts-ignore */}
          {ToolComponent && <ToolComponent renderContainer={false} />}
        </div>

        <div className={blogPostStyles.content} data-guide={post.type === 'guide-post'}>
          <div>
            <GuideHeaderText post={post} postConfig={postConfig} />

            <ContentsTool post={post} postConfig={postConfig} scrollPos={0} staticMode />
            <div
              className={classNames(styles.blogPost, { [styles.noSpaces]: postConfig.isSSG || postConfig.isSIG })}
              dangerouslySetInnerHTML={{ __html: post.html || '' }}
            />
          </div>
        </div>
      </div>

      <CommonFooter className={styles.footer} light />

      <IconsLoader post={post} />
    </div>
  )
}

export default SeoToolContainer
